import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import ImageLoader from '../components/ImageLoader';

// Smooth scroll helper function
const scrollToSection = (id: string) => {
  const element = document.getElementById(id);
  if (element) {
    element.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  }
};

// Voeg de CustomImage component toe
const CustomImage = ({ src, alt, className }: { src: string, alt: string, className?: string }) => {
  return (
    <img 
      src={`${process.env.PUBLIC_URL}/${src.replace(/^\//, '')}`} 
      alt={alt} 
      className={className || ''} 
    />
  );
};

const Home: React.FC = () => {
  const [currentImage, setCurrentImage] = useState(0);

  // Image slideshow effect for hero section
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImage((prev) => (prev + 1) % 5);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  // Handle anchor links with smooth scrolling on page load
  useEffect(() => {
    // Get hash from URL
    const hash = window.location.hash;
    if (hash) {
      // Remove the # symbol
      const id = hash.substring(1);
      // Wait a moment for page to render completely
      setTimeout(() => {
        scrollToSection(id);
      }, 500);
    }
  }, []);

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative h-screen">
        <div className="absolute inset-0 bg-black bg-opacity-40 flex items-center justify-center text-center z-10">
          <div className="px-4 transform transition-all duration-700 hover:scale-105">
            <h1 className="text-5xl md:text-6xl font-display font-bold text-white mb-6 drop-shadow-lg">
              Tuk Tuk Restaurant
            </h1>
            <p className="text-xl md:text-2xl text-white mb-10 font-body drop-shadow-lg">
              Experience the Unseen Flavors of Cambodia
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <Link to="/menu" className="bg-tuktuk-red hover:bg-red-700 text-white font-bold py-3 px-8 rounded-lg transition duration-300 transform hover:-translate-y-1">
                View Menu
              </Link>
              <Link to="/reservations" className="bg-tuktuk-green hover:bg-green-700 text-white font-bold py-3 px-8 rounded-lg transition duration-300 transform hover:-translate-y-1">
                Book a Table
              </Link>
              <Link to="/farm-tours" className="bg-tuktuk-brown hover:bg-yellow-700 text-white font-bold py-3 px-8 rounded-lg transition duration-300 transform hover:-translate-y-1">
                Local Farm Tours
              </Link>
            </div>
          </div>
        </div>
        {/* Hero Image Slideshow */}
        <div className="absolute inset-0 -z-0">
          <div className="relative h-full w-full overflow-hidden">
            {[0, 1, 2, 3, 4].map((idx) => (
              <div 
                key={idx} 
                className={`absolute inset-0 transition-opacity duration-1000 ${
                  currentImage === idx ? 'opacity-100' : 'opacity-0'
                }`}
              >
                <ImageLoader 
                  index={idx} 
                  alt={`Cambodian cuisine and culture ${idx + 1}`} 
                  className="h-full w-full object-cover"
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Welcome/About Us Section */}
      <section id="about" className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-display font-bold text-center text-tuktuk-brown mb-8">
            A Culinary Journey Through Cambodia
          </h2>
          <div className="max-w-3xl mx-auto text-center">
            <p className="text-lg mb-6">
              Welcome to Tuk Tuk Restaurant, where culinary artistry meets cultural immersion. Nestled in the heart of Siem Reap, 
              we offer more than just meals—we provide unforgettable journeys through the rich tapestry of Cambodian flavors and traditions.
            </p>
            <p className="text-lg mb-8">
              Our Cambodian chef-owner brings his unique experience from cooking in Japan to create dishes that honor traditional 
              recipes while introducing subtle fusion elements. Every dish tells a story of Cambodia's rich heritage, prepared with 
              love and expertise.
            </p>
            <Link to="/about" className="inline-block bg-tuktuk-brown text-white font-bold py-2 px-6 rounded-lg hover:bg-opacity-80 transition duration-300">
              Discover Our Story
            </Link>
          </div>
        </div>
      </section>

      {/* Farm to Table Section */}
      <section id="farm" className="py-16 bg-tuktuk-beige bg-opacity-30">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center">
            <div className="md:w-1/2 mb-8 md:mb-0 md:pr-8">
              <h2 className="text-4xl font-display font-bold text-tuktuk-brown mb-6">
                From Farm to Table
              </h2>
              <p className="text-lg mb-4">
                Embark on an enlightening journey to our local farm, where the freshest ingredients 
                for our dishes are cultivated. Experience firsthand the traditional farming methods 
                that have sustained Cambodian communities for generations.
              </p>
              <p className="text-lg mb-6">
                Our commitment to authentic, locally-sourced ingredients ensures that every 
                dish not only delights your palate but also connects you to the soul of Cambodia.
              </p>
              <Link to="/farm-tours" className="inline-block bg-tuktuk-green text-white font-bold py-2 px-6 rounded-lg hover:bg-green-700 transition duration-300">
                Explore Our Farm Tours
              </Link>
            </div>
            <div className="md:w-1/2 h-80 rounded-lg overflow-hidden shadow-xl">
              <ImageLoader 
                index={5} 
                alt="Our farm in Cambodia" 
                className="w-full h-full object-cover transform transition-transform duration-700 hover:scale-110"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Rental Service Section */}
      <section id="rental" className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-display font-bold text-center text-tuktuk-brown mb-8">
            Explore Siem Reap With Our Rentals
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 max-w-5xl mx-auto">
            <div className="bg-tuktuk-beige bg-opacity-20 rounded-lg shadow-lg p-6 transition-transform hover:-translate-y-1">
              <h3 className="text-2xl font-display font-bold text-tuktuk-brown mb-4">Scooter Rentals</h3>
              <div className="space-y-3 mb-4">
                <div className="flex justify-between border-b border-gray-300 pb-2">
                  <span className="font-semibold">Yamaha Qbix 2022</span>
                  <span className="text-tuktuk-red font-bold">$20/day</span>
                </div>
                <div className="flex justify-between border-b border-gray-300 pb-2">
                  <span className="font-semibold">Honda Click 2018</span>
                  <span className="text-tuktuk-red font-bold">$13/day</span>
                </div>
                <div className="flex justify-between border-b border-gray-300 pb-2">
                  <span className="font-semibold">Honda Click 2021</span>
                  <span className="text-tuktuk-red font-bold">$13/day</span>
                </div>
                <div className="flex justify-between border-b border-gray-300 pb-2">
                  <span className="font-semibold">Honda Dream</span>
                  <span className="text-tuktuk-red font-bold">$13/day</span>
                </div>
                <div className="flex justify-between border-b border-gray-300 pb-2">
                  <span className="font-semibold">Honda Zoomer-X 2018</span>
                  <span className="text-tuktuk-red font-bold">$13/day</span>
                </div>
              </div>
              <p className="text-sm text-gray-600 italic mb-4">
                Insurance up to 50% on motorbike crash or any accident may have. 
                Hidden cost or commission will be added.
              </p>
              <button 
                onClick={() => window.open('https://wa.me/817044761574?text=I%20am%20interested%20in%20renting%20a%20scooter', '_blank')} 
                className="bg-tuktuk-red hover:bg-red-700 text-white font-bold py-2 px-4 rounded-lg transition duration-300"
              >
                Rent Now
              </button>
            </div>
            
            <div className="bg-tuktuk-beige bg-opacity-20 rounded-lg shadow-lg p-6 transition-transform hover:-translate-y-1">
              <h3 className="text-2xl font-display font-bold text-tuktuk-brown mb-4">Bicycle Rentals</h3>
              <div className="space-y-3 mb-4">
                <div className="flex justify-between border-b border-gray-300 pb-2">
                  <span className="font-semibold">Mountain Bike</span>
                  <span className="text-tuktuk-red font-bold">$6/day</span>
                </div>
                <div className="flex justify-between border-b border-gray-300 pb-2">
                  <span className="font-semibold">City Bike</span>
                  <span className="text-tuktuk-red font-bold">$3/day</span>
                </div>
              </div>
              <p className="text-gray-700 mt-4 mb-4">
                Explore the charming streets of Siem Reap and its surroundings at your own pace with our 
                comfortable and well-maintained bicycles. Perfect for visiting nearby temples or enjoying 
                a leisurely ride through the countryside.
              </p>
              <div className="mt-2">
                <h4 className="font-semibold mb-2">Each rental includes:</h4>
                <ul className="list-disc pl-5 space-y-1 mb-4">
                  <li>Helmet</li>
                  <li>Lock</li>
                  <li>Basic repair kit</li>
                  <li>Map of recommended routes</li>
                </ul>
              </div>
              <button 
                onClick={() => window.open('https://wa.me/817044761574?text=I%20am%20interested%20in%20renting%20a%20bicycle', '_blank')} 
                className="bg-tuktuk-brown hover:bg-opacity-80 text-white font-bold py-2 px-4 rounded-lg transition duration-300"
              >
                Rent Now
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Special Dishes Section */}
      <section id="specialties" className="py-16 bg-tuktuk-beige bg-opacity-30">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-display font-bold text-center text-tuktuk-brown mb-12">
            Signature Dishes
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Dish 1 */}
            <div className="bg-white rounded-lg overflow-hidden shadow-xl transform transition-all duration-300 hover:shadow-2xl hover:-translate-y-2">
              <div className="h-64 overflow-hidden">
                <CustomImage 
                  src="amokkkk.jpg" 
                  alt="Amok Trey" 
                  className="w-full h-full object-cover transition-transform duration-500 hover:scale-110"
                />
              </div>
              <div className="p-6">
                <h3 className="text-2xl font-display font-bold text-tuktuk-brown mb-2">
                  Amok Trey
                </h3>
                <p className="text-gray-700 mb-4">
                  Traditional Cambodian curry with fish, coconut milk, and herbs, steamed in banana leaf.
                </p>
                <span className="text-tuktuk-red font-bold">$3.00</span>
              </div>
            </div>
            
            {/* Dish 2 */}
            <div className="bg-white rounded-lg overflow-hidden shadow-xl transform transition-all duration-300 hover:shadow-2xl hover:-translate-y-2">
              <div className="h-64 overflow-hidden">
                <CustomImage 
                  src="loklak.jpg" 
                  alt="Lok Lak" 
                  className="w-full h-full object-cover transition-transform duration-500 hover:scale-110"
                />
              </div>
              <div className="p-6">
                <h3 className="text-2xl font-display font-bold text-tuktuk-brown mb-2">
                  Lok Lak
                </h3>
                <p className="text-gray-700 mb-4">
                  Marinated beef with lime-pepper sauce and fresh vegetables, a beloved Cambodian classic.
                </p>
                <span className="text-tuktuk-red font-bold">$3.00</span>
              </div>
            </div>
            
            {/* Dish 3 */}
            <div className="bg-white rounded-lg overflow-hidden shadow-xl transform transition-all duration-300 hover:shadow-2xl hover:-translate-y-2">
              <div className="h-64 overflow-hidden">
                <CustomImage 
                  src="bbq-dish.jpg" 
                  alt="BBQ Dish" 
                  className="w-full h-full object-cover transition-transform duration-500 hover:scale-110"
                />
              </div>
              <div className="p-6">
                <h3 className="text-2xl font-display font-bold text-tuktuk-brown mb-2">
                  BBQ Dish
                </h3>
                <p className="text-gray-700 mb-4">
                  Succulent barbecued meat served with an assortment of fresh vegetables and our special house sauce.
                </p>
                <span className="text-tuktuk-red font-bold">$10.00</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="testimonials" className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-display font-bold text-center text-tuktuk-brown mb-12">
            What Our Guests Say
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-lg shadow-lg">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-tuktuk-red rounded-full flex items-center justify-center text-white font-bold text-xl mr-4">S</div>
                <div>
                  <h4 className="font-bold">Sarah Thompson</h4>
                  <p className="text-gray-600">Travel Blogger</p>
                </div>
              </div>
              <p className="italic">"The farm tour was an unexpected highlight of my trip to Siem Reap. Learning about traditional 
              Cambodian farming practices and then tasting those same ingredients in an expertly prepared meal was an 
              experience I'll never forget!"</p>
            </div>
            <div className="bg-white p-8 rounded-lg shadow-lg">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-tuktuk-green rounded-full flex items-center justify-center text-white font-bold text-xl mr-4">J</div>
                <div>
                  <h4 className="font-bold">James Chen</h4>
                  <p className="text-gray-600">Food Critic</p>
                </div>
              </div>
              <p className="italic">"Tuk Tuk Restaurant offers the most authentic Cambodian cuisine I've experienced outside 
              of a local family home. The Amok Trey was perfectly seasoned, and the staff's knowledge 
              about each dish's cultural significance added a wonderful dimension to our dinner."</p>
            </div>
            <div className="bg-white p-8 rounded-lg shadow-lg">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 overflow-hidden rounded-full mr-4">
                  <ImageLoader 
                    index={18} 
                    alt="Mei Lin"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div>
                  <h4 className="font-bold">Mei Lin</h4>
                  <p className="text-gray-600">Food Enthusiast</p>
                </div>
              </div>
              <p className="italic">"The Lok Lak here is absolutely amazing! The beef was perfectly cooked and the dipping sauce with lime added that perfect tangy flavor. The colorful atmosphere and friendly staff made our evening even more special. Can't wait to come back!"</p>
            </div>
          </div>
        </div>
      </section>

      {/* Restaurant Atmosphere Gallery */}
      <section id="gallery" className="py-16 bg-tuktuk-beige bg-opacity-20">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-display font-bold text-center text-tuktuk-brown mb-8">
            Experience Our Atmosphere
          </h2>
          <p className="text-center max-w-2xl mx-auto mb-12 text-lg">
            Step into our vibrant restaurant where every corner tells a story. From our colorful dining area to the authentic Cambodian decor, we've created a space where memories are made.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="h-64 rounded-lg overflow-hidden shadow-lg transform transition duration-300 hover:scale-105">
              <ImageLoader 
                index={16} 
                alt="Restaurant interior with guests dining" 
                className="w-full h-full object-cover"
              />
            </div>
            <div className="h-64 rounded-lg overflow-hidden shadow-lg transform transition duration-300 hover:scale-105">
              <ImageLoader 
                index={17} 
                alt="Restaurant customers enjoying dinner" 
                className="w-full h-full object-cover"
              />
            </div>
            <div className="h-64 rounded-lg overflow-hidden shadow-lg transform transition duration-300 hover:scale-105">
              <ImageLoader 
                index={19} 
                alt="Evening atmosphere at Tuk Tuk Restaurant" 
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section id="contact" className="py-16 bg-tuktuk-brown text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-display font-bold mb-6">Ready for a Culinary Adventure?</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Join us at Tuk Tuk Restaurant for an unforgettable journey through the flavors of Cambodia
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <a 
              href="https://wa.me/817044761574" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="inline-block bg-green-500 text-white font-bold py-3 px-8 rounded-lg hover:bg-green-600 transition duration-300 transform hover:-translate-y-1 flex items-center justify-center"
            >
              <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
              </svg>
              WhatsApp Reservation
            </a>
            <Link 
              to="/reservations" 
              className="inline-block bg-white text-tuktuk-brown font-bold py-3 px-8 rounded-lg hover:bg-tuktuk-beige transition duration-300 transform hover:-translate-y-1"
            >
              Reserve Your Table Now
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home; 